import { Injectable, OnDestroy } from '@angular/core';

import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subscription,
  catchError,
  of,
  retry,
  shareReplay,
  take,
  tap,
  timeout,
} from 'rxjs';
import { Router } from '@angular/router';
import { API } from '../configs/api.config';
import { GeneralService } from './general.service';
import { ErrorService } from './error.service';
import { NotificationService } from './notification.service';
import { SSENotificationService } from './sse-notification.service';
import { UserProfileService } from './user-profile.service';
import { LoginInput, LoginMFAInput, LoginOutput } from '../models/auth.model';
import { LS } from '../enums/local-storage.enum';
import { ClarityService } from './clarity.service';
import { environment } from 'projects/sharing/environments/environment';
import { ResponseDataType } from '../models/general.model';
import { UserRole } from '../enums/role.enum';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  actionId = {
    login: environment.LOGIN_ACTION_ID,
    logout: environment.LOGOUT_ACTION_ID,
  };
  menuId = {
    login: environment.LOGIN_MENU_ID,
    logout: environment.LOGOUT_MENU_ID,
  };
  private readonly subscriptions: Subscription[] = [];

  private readonly isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public readonly isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private readonly authReadySubject = new BehaviorSubject<boolean>(false);
  public readonly authReady$ = this.authReadySubject.asObservable();

  private sessionCheck$: Observable<unknown> | undefined;
  private sessionCheckGeneration = 0;
  private authGeneration = 0;

  private static readonly MAX_LOGOUT_RETRY = 3;
  private static readonly SESSION_CHECK_TIMEOUT_MS = 10_000;
  private logoutHttpAttemptCount = 0;
  private forcedLogoutFired = false;

  getAuthGeneration(): number {
    return this.authGeneration;
  }

  constructor(
    private readonly router: Router,
    private readonly generalService: GeneralService,
    private readonly errorService: ErrorService,
    private readonly clarityService: ClarityService,
    private readonly notificationService: NotificationService,
    private readonly userProfileService: UserProfileService,
    private readonly sseNotificationService: SSENotificationService,
  ) {}

  login(
    params: LoginInput,
    turnstileToken: string,
  ): Observable<ResponseDataType<LoginOutput>> {
    return this.generalService.postWithCaptchaToken(
      params,
      API.LOGIN,
      turnstileToken,
      API.AUTH_BASE_URL,
      this.menuId.login,
      this.actionId.login,
    );
  }

  loginWithMFA(
    params: LoginMFAInput,
    token: string,
  ): Observable<ResponseDataType<LoginOutput>> {
    return this.generalService.postWithCustomToken(
      params,
      API.LOGIN_MFA,
      token,
      API.AUTH_BASE_URL,
      this.menuId.login,
      this.actionId.login,
    );
  }

  setUserSession(data: LoginOutput): void {
    window.localStorage.setItem(LS.USER_EMAIL, data.email);
    window.localStorage.setItem(LS.USER_FULLNAME, data.fullName);
    window.localStorage.setItem(LS.ROLE_ID, data.roleId);
  }

  private triggerPostLoginSyncInBackground(): void {
    const subs = this.userProfileService
      .getUserProfile()
      .pipe(
        retry(2),
        catchError(() => EMPTY),
      )
      .subscribe();
    this.subscriptions.push(subs);
  }

  logout(opts: { force?: boolean } = {}): boolean {
    this.clearExpiredSession();
    this.sseNotificationService.stopSSEConnection();

    if (opts.force) {
      if (this.forcedLogoutFired) {
        return false;
      }
      this.forcedLogoutFired = true;
    } else {
      if (this.logoutHttpAttemptCount >= AuthService.MAX_LOGOUT_RETRY) {
        return false;
      }
      this.logoutHttpAttemptCount++;
    }
    this.subscriptions.push(
      this.generalService
        .postData(
          {},
          API.USERS_LOGOUT,
          API.AUTH_BASE_URL,
          this.menuId.logout,
          this.actionId.logout,
        )
        .subscribe({
          error: (err: { status?: number }) => {
            console.warn('[AuthService] logout HTTP failed', err?.status);
          },
        }),
    );
    return true;
  }

  clearAuth(): void {
    window.localStorage.removeItem(LS.USER_TOKEN_TIME);
    window.localStorage.removeItem(LS.USER_REFRESH_TOKEN);
    this.errorService.setData(false);
    window.localStorage.setItem(LS.LAST_URL, this.router.url);
    window.localStorage.removeItem(LS.MERCHANT_LIST);
    window.localStorage.removeItem(LS.MENU_LIST);
    window.localStorage.removeItem(LS.PERMISSION_LIST);
    this.notificationService.clearDisplayState();
    this.authGeneration++;
    this.sessionCheckGeneration++;
    this.isAuthenticatedSubject.next(false);
    this.authReadySubject.next(false);
    this.sessionCheck$ = undefined;
  }

  clearExpiredSession(): void {
    this.clearAuth();
    this.clarityService.stopTracking(true);
  }

  /**
   * Clears all client-side session state exactly like logout, but without
   * firing the logout HTTP call. Used when the session is already dead on
   * the server side (e.g. a 401 session-expired response) so a logout
   * request would be pointless.
   */
  softLogout(): void {
    this.clearExpiredSession();
    this.sseNotificationService.stopSSEConnection();
  }

  initializeSession(): void {
    if (!this.sessionCheck$) {
      const generation = ++this.sessionCheckGeneration;
      this.sessionCheck$ = this.userProfileService.getUserProfile().pipe(
        timeout(AuthService.SESSION_CHECK_TIMEOUT_MS),
        tap(() => {
          if (generation === this.sessionCheckGeneration) {
            this.forcedLogoutFired = false;
            this.isAuthenticatedSubject.next(true);
          }
        }),
        catchError(() => {
          if (generation === this.sessionCheckGeneration) {
            this.isAuthenticatedSubject.next(false);
          }
          return of(null);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
        take(1),
      );
    }
    this.sessionCheck$.subscribe({
      complete: () => this.authReadySubject.next(true),
      error: () => this.authReadySubject.next(true),
    });
  }

  markAuthenticated(): void {
    this.authGeneration++;
    this.isAuthenticatedSubject.next(true);
    this.authReadySubject.next(true);

    this.sessionCheckGeneration++;
    this.sessionCheck$ = undefined;

    this.logoutHttpAttemptCount = 0;
    this.forcedLogoutFired = false;

    this.triggerPostLoginSyncInBackground();
  }

  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  getRole(): UserRole {
    const roleId = window.localStorage.getItem(LS.ROLE_ID);
    if (!roleId) {
      return UserRole.UNKNOWN;
    }
    // Map role IDs to enum values based on environment constants
    const roleMapping: Record<string, UserRole> = {
      [environment.SALES_ROLE_ID]: UserRole.SALES,
      [environment.MS_ROLE_ID]: UserRole.MERCHANT_SUPPORT,
      [environment.LEGAL_ROLE_ID]: UserRole.LEGAL,
      [environment.RM_ROLE_ID]: UserRole.RM,
      [environment.SUPER_ADMIN_ROLE_ID]: UserRole.SUPER_ADMIN,
    };
    return roleMapping[roleId] || UserRole.UNKNOWN;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((el) => el.unsubscribe());
  }
}
